/**
 * Everything the league writes, in one place.
 *
 * The app is replacing the Draftboard spreadsheet, so every tab of that sheet that a manager
 * touches needs a home here: keeper declarations (Keepers tab), draft slot preferences
 * (PickPrefs), trades (Trade Log), the farm auction (Farm Log) and the picks themselves
 * (Draftboard). FAAB is not a tab — it is scattered across the sheet in formulas — so it gets
 * a proper ledger.
 *
 * These are storage shapes, deliberately dumb: no methods, no derived values, nothing the
 * rules engine should own. Anything computed (a keeper's cost, a GM's luxury tax, whether a
 * slate is legal) is derived from these by src/domain, never persisted. That way a rule change
 * re-prices history instead of leaving stale numbers behind — which is exactly the failure
 * mode the spreadsheet has.
 */

import type { GateException } from '../domain/deadlines.js';
import type { ProposalEffect } from '../domain/effects.js';
import type { ProxyBallot } from '../domain/proxy.js';
import type { Override } from '../domain/overrides.js';
import type { PlayerBank } from '../domain/playerBank.js';
import type { ProspectBank } from '../domain/prospectBank.js';

/** Uppercase manager name — MATT, RAJAN. Stable across seasons; team names are not. */
export type GmId = string;

/**
 * Four levels, ordered. A manager acts for his own team; a commissioner runs the league; an admin
 * owns the app itself and can do anything either of them can, plus act as another GM to fix
 * something on their behalf.
 *
 * Rajan and Vishal are the commissioners — the rulebook speaks of "the Commissioners" in the
 * plural (2020-II-1 §5) and has done for years.
 *
 * `VIEWER` IS BELOW MANAGER, AND THAT IS THE WHOLE DESIGN OF IT.
 *
 * Neel signs in as a co-manager on Yahoo and never touches anything — he watches the draft go by
 * and says things about it in Discord. Everything here that is not a control is worth him seeing:
 * the board, the auction, the records, the rulebook. Nothing that IS a control should be
 * reachable, and he holds no team for one to belong to.
 *
 * Modelling that as a rank UNDER manager rather than as a flag means every `atLeast(role,
 * 'manager')` already written — and there are many — starts refusing him without a single call
 * site changing. A flag would have needed each one found and edited, and the ones that were
 * missed would be exactly the ones nobody thought to check.
 *
 * A viewer is not an unlinked account. An unlinked account is somebody the app has not worked out
 * yet, and the sign-in screen says so. A viewer is a decision an admin made.
 */
export type Role = 'viewer' | 'manager' | 'commissioner' | 'admin';

/** Higher wins. Use `atLeast` rather than comparing strings anywhere else. */
export const ROLE_RANK: Record<Role, number> = {
  viewer: -1, manager: 0, commissioner: 1, admin: 2,
};

export const atLeast = (role: Role | undefined, min: Role): boolean =>
  ROLE_RANK[role ?? 'manager'] >= ROLE_RANK[min];

/**
 * May this person act at all?
 *
 * The question `atLeast` answers badly on its own, because most screens ask "is this his own
 * row?" rather than "what rank is he?" — and a viewer's answer is no whatever row you show him,
 * since he has no team to own one. Written once so no screen has to remember.
 */
export const canAct = (role: Role | undefined): boolean => atLeast(role, 'manager');

export interface Session {
  /** The team being acted for. For an admin using "view as", this is the OTHER GM. */
  gm: GmId;
  /**
   * The role the app should ENFORCE right now. While impersonating this drops to the role of the
   * person being impersonated, so an admin looking at the league as a manager is bound by the
   * same deadlines and locked out of the same controls. Otherwise "view as" shows you a screen
   * nobody else can see and quietly lies about what they can do.
   */
  role: Role;
  /** The role you actually hold. Only used to decide who may impersonate at all. */
  realRole?: Role;
  displayName: string;
  /**
   * What the server worked out about this Yahoo account, when it could not confirm a manager.
   * Carried on the session so the sign-in screen can say WHY somebody is not recognised —
   * "Yahoo says you own X in league 338028, but nobody on the roster has a team by that name"
   * is actionable; "not authorised" is not.
   */
  /*
   * A THIRD SHAPE FOR THE SAME ROW, which is one too many — `AccountLink` in `session.tsx` and
   * `AccessLink` in `domain/access.ts` are the other two, and they have already drifted: this one
   * was missing `rejected` when rejection shipped, so the sign-in screen could not tell a stranger
   * who had been turned away from one nobody had got to yet. Widened rather than unified, because
   * they are three genuinely different slices of one server document — but the next field that has
   * to be added in three places should collapse them instead.
   */
  link?: {
    gm: string | null; confirmed: boolean; teamName: string | null; why: string;
    rejected?: boolean;
  };
  /**
   * Set only when an admin is acting as somebody else. Everything the app writes records the
   * real actor, so an admin fixing a slate on a manager's behalf is visible rather than
   * indistinguishable from the manager doing it himself.
   */
  actingAs?: GmId;
  realGm?: GmId;
  /** Absent in the offline preview, where sign-in is a picker rather than a real identity. */
  uid?: string;
  email?: string;
}

/* ---------------------------------------------------------------------------------------- */

/**
 * One player a GM intends to keep. The COST IS NOT STORED — it is computed from the roster
 * record and the rules in force, so a mid-August rule correction reprices every declaration
 * automatically instead of leaving the sheet's numbers frozen and wrong.
 */
export interface DeclaredKeeper {
  player: string;
  /** He is being kept under the franchise tag (2025 P05): his own round, three keeper slots. */
  franchiseTagged?: boolean;
  /**
   * A farm graduate who never made an opening-day 53-man roster.
   *
   * He STAYS ON THE SLATE, which is the point: 2019-II-5 §1 forfeits the keeper spot outright,
   * forbids declaring somebody "instead or in place of" him, and still charges luxury tax as
   * though he had been kept. Dropping him from the slate would hand the GM the slot back, which
   * is the one reading the rules never supported. He is simply not on the roster — 2015-III-1
   * ends the obligation to carry him, and 2022-farm-system-clarification ends it for the IR/PUP
   * case too.
   *
   * So: counted by `assessLuxuryTax` in `slotsUsed`, excluded from `effectiveKeepers`, and given
   * no pick on the draft board.
   */
  farmForfeited?: boolean;
  /**
   * The GM has manually moved him off his computed round to resolve a collision with another
   * of his own keepers. 2015-III-7 forces SOME resolution but leaves the choice to the GM —
   * Rajan: "you can effectively escalate whomever you want, based on their overlaps."
   */
  elevatedTo?: number;
  /**
   * The actual pick he was charged to when the slate was saved: round plus the ORIGINAL
   * owner's team, which is what numbers the cell on the board. Written on every save, for
   * every keeper.
   *
   * This is a RECORD, not a pin — the two are different on purpose. `elevatedTo` is the GM's
   * round-level choice and is honoured on load, so a rules correction can still reprice the
   * slate (costs derive on read; see the interface comment above). `chargedPick` is what that
   * derivation resolved to at save time, and the only part of it read back is `from`, the
   * within-round choice — which round it lands in stays derived. Without this field, a GM
   * holding two picks in one round (John's two 9ths, after the November trade) could not say
   * which one paid for his keeper, and keeper history reads "Round 9 (pick not logged)".
   */
  chargedPick?: { round: number; from: string };
}

export interface KeeperDeclaration {
  season: number;
  gm: GmId;
  keepers: DeclaredKeeper[];
  /** Submitted slates are what the commissioner reviews; drafts are private to the GM. */
  status: 'draft' | 'submitted';
  updatedAt: string;
  submittedAt?: string;
  /** Who actually saved it. Differs from `gm` when a commissioner or admin edited it for them. */
  updatedBy?: GmId;
  /** Free text the GM wants the commissioner to see — the Discord back-and-forth, in place. */
  note?: string;
}

/**
 * One manager's GUESS at what the other nine teams will keep.
 *
 * PRIVATE TO ITS AUTHOR, and that is the whole design. Reading somebody else's read on the market
 * is worth more than reading his slate — a slate is one team, a set of guesses is his valuation of
 * everybody — so this is the only collection in the app that is not league-readable. The rules
 * file enforces it; the screen not showing it is not enforcement.
 *
 * SEEDED ONLY FROM SUBMITTED SLATES. A submitted declaration is public the moment it is submitted
 * and is copied in as a starting point; a DRAFT slate is the manager still thinking and is never
 * used, whatever the database would let this code read. Everything else is typed in by hand,
 * which is the point of the exercise.
 */
export interface MockKeeperSet {
  season: number;
  /** Whose guesses these are. Also the document id, which is what makes ownership checkable. */
  gm: GmId;
  /** Guessed keepers per team, keyed by the GM being guessed about. */
  byGm: Record<GmId, MockKeeper[]>;
  /**
   * What the real submitted slates looked like when this was last synced, so the screen can tell
   * "he has since changed his slate" from "I guessed differently on purpose".
   */
  syncedFrom?: Record<GmId, string[]>;
  updatedAt: string;
}

export interface MockKeeper {
  player: string;
  /** True when this row was copied from a submitted slate rather than guessed. */
  fromDeclaration?: boolean;
  franchiseTagged?: boolean;
  /** The GM's own round choice, honoured the same way a real declaration's is (2015-III-7). */
  elevatedTo?: number;
}

/* ---------------------------------------------------------------------------------------- */

/** PickPrefs tab: a ranked list of draft slots, resolved in finishing order. */
export interface DraftPreference {
  season: number;
  gm: GmId;
  /** Slot numbers in preference order, best first. */
  ranked: number[];
  updatedAt: string;
  /**
   * When he pressed Submit, ISO. Null while he is still ranking.
   *
   * The 2026 design autosaves the list as it changes and keeps the button for submitting, which
   * splits a thing that used to be one: "has a ranking" stopped meaning "has submitted" the moment
   * the first tap started writing. The commissioner's screen counts submissions before it runs an
   * irreversible draw, so it needs the second meaning, not the first.
   *
   * Absent on anything written before the split — read as not submitted, which is the safe way
   * round: it names a GM in the warning who may in fact be ready, rather than running past one who
   * is not.
   */
  submittedAt?: string | null;
}

/* ---------------------------------------------------------------------------------------- */

/** One side of a trade. Picks are "2026 9.4" style; FAAB is whole dollars. */
export interface TradeSide {
  gm: GmId;
  players: string[];
  picks: string[];
  faab: number;
  /** Keeper RIGHTS to a farm player, which trade separately from the player himself. */
  rights: string[];
}

/**
 * Trades are DECLARED in Discord #trade-declarations — that stays the venue, because the
 * 24-hour counter-offer window (2013-II-1) happens in conversation. This is the ledger the
 * declaration lands in afterwards, and it is what pick ownership is computed from.
 */
export interface Trade {
  id: string;
  season: number;
  date: string;
  sides: [TradeSide, TradeSide];
  /** Link back to the Discord message, so every row can be checked against the declaration. */
  sourceUrl?: string;
  note?: string;
  /**
   * Struck from the log.
   *
   * A TOMBSTONE RATHER THAN A DELETION, because most of the log is not deletable: 109 of the
   * trades are bundled in `trade-log.json` and are read from the app's own build, not from the
   * database. A commissioner striking one of those has nothing to delete — so he writes a record
   * saying it did not happen, and the reader drops it. The original stays in the file, which is
   * also what makes the strike reversible.
   */
  voided?: boolean;
  /**
   * WHAT BECAME OF THE OPTION.
   *
   * An "other consideration" is recorded verbatim and owns nothing — until the day it is used.
   * The 19 November 2025 clause gives John the right to swap 6th-round picks with Rajan once the
   * order is set, and he may decline. Either answer is a fact about the trade the clause came
   * from, so it is stored on that row: `exercised` points at the ledger row the swap wrote,
   * `declined` closes the question without moving anything. Absent means still open.
   */
  clauseOutcome?: {
    outcome: 'exercised' | 'declined';
    /** The GM whose option it was. */
    by: GmId;
    at: string;
    /** The ledger row the exercise wrote, where picks moved. */
    tradeId?: string;
    recordedBy: GmId;
  };
  recordedBy: GmId;
  recordedAt: string;
}

/* ---------------------------------------------------------------------------------------- */

/**
 * A trade between "we agreed" and "it is in the log".
 *
 * Separate from `Trade` on purpose. `Trade` is the LEDGER — 109 rows of settled history that
 * pick ownership is computed from, and nothing in flight belongs in it: a deal awaiting
 * affirmation has not happened, and a deal inside its counter window may still be replaced by a
 * counter or killed by six vetoes. Putting them in one collection would mean every reader of the
 * ledger having to remember to exclude the ones that are not real yet, and the one that forgets
 * is the one that computes who owns the 2026 third.
 *
 * So: a live trade is written here, and lands in `trades` once — when its window closes with it
 * intact. The rules and the clock are in `domain/tradeWindow.ts`.
 */
export interface LiveTradeDoc {
  id: string;
  season: number;
  /** [filer, counterparty]. Index 0 is always the man who filed it. */
  sides: [LiveTradeSide, LiveTradeSide];
  filedBy: GmId;
  filedAt: string;
  /**
   * The instant every clock on the screen is measured from: the counterparty's affirmation, or
   * the time a commissioner records for a deal declared outside the app. Absent until one of
   * those happens, and while it is absent the trade has no window and cannot expire.
   */
  agreedAt?: string | null;
  agreedSource?: 'affirmation' | 'commissioner';
  /** Who wrote `agreedAt` — the affirming GM, or the commissioner who entered it. */
  agreedBy?: GmId;
  counters: LiveTradeCounter[];
  /** GM ids that have declared one. 2016-I-3 needs six of the eight uninvolved. */
  vetoes: GmId[];
  /** "Other consideration" — recorded verbatim, never converted into an asset anyone owns. */
  clauses: string[];
  withdrawn?: boolean;
  /**
   * The trades that replaced this one. Plural because 2015-II-2 lets each party accept a different
   * counter inside the same window, and both survivors stand.
   */
  replacedBy?: string[];
  /** On a trade born from an accepted counter: the trade whose window it inherits. */
  derivedFrom?: string;
  /** Which offer was taken, and when. */
  accepted?: { counterId: string; at: string };
  /**
   * A commissioner closed the window early (Tim, 8 September: "add functionality as commish to
   * push the trade through before the window is complete").
   *
   * The window is otherwise pure arithmetic on `agreedAt`, and nothing in the app could shorten
   * it — the only lever was to move the agreed time backwards, which is `corrections` lying about
   * when two men shook hands. This is the honest version: the deal keeps the time it was struck,
   * and the record says a person ended the waiting and who.
   *
   * It ends the COUNTER and VETO period, which is the whole content of the window, so it is not a
   * small act. What it cannot do is revive a trade the league already killed: six vetoes are
   * checked first and still stand. `reason` is optional and printed on the card if given.
   */
  forced?: { by: GmId; at: string; reason?: string };
  /** Set when the window closed with it intact and a commissioner wrote it into `trades`. */
  settledAs?: string;
  /**
   * A commissioner's session is settling this one right now, and when it started.
   *
   * Filing happens on its own once the window closes, so two commissioners with the app open
   * would otherwise both do it. This is the lease that stops them reading each other's half-written
   * declarations; it expires, deliberately, so a session that died mid-settle cannot hold a trade
   * hostage. See `domain/tradeFiling.ts` for why a lease and not a transaction.
   */
  settling?: { by: GmId; at: string };
  /**
   * A commissioner's corrections to a trade already in its window, oldest first.
   *
   * A deal recorded from Discord gets its time typed by a person, and 1 September had one with
   * the wrong one — a clock counting from 7:46 PM for a trade agreed at 2:23. The window is
   * measured from `agreedAt`, so a wrong time is a wrong deadline for the whole league, and the
   * only fix before this was withdrawing and re-recording, which the rules do not allow once a
   * trade is agreed. Each entry says what changed and who changed it, so the card can print it
   * and nobody has to trust that the number they see now is the number that was always there.
   */
  corrections?: LiveTradeCorrection[];
  /**
   * WHO TAKES THE HIT, chosen by the GM whose keepers move: gm -> player -> round.
   *
   * A trade that takes a keeper's pick away forces some re-charge (2015-III-7), and the rule
   * leaves the choice of which keeper moves to the GM — Rajan: "you can effectively escalate
   * whomever you want." The chooser on the affirmation card writes his answer here, and
   * settlement applies it as pins over the resolver's default. Absent, the resolver's own fit
   * stands, which is the arrangement every other screen would compute.
   */
  keeperPins?: Record<string, Record<string, number>>;
}

export interface LiveTradeCorrection {
  at: string;
  by: GmId;
  /** `agreedAt` or `terms` — the two things a commissioner may put right. */
  field: 'agreedAt' | 'terms';
  /** Both sides in words, so the log reads without the original document. */
  from: string;
  to: string;
}

export interface LiveTradeSide {
  gm: GmId;
  /** What this GM SENDS. The card renders the other man's sends as what he gets. */
  sends: LiveTradeAsset[];
}

export interface LiveTradeAsset {
  kind: 'player' | 'pick' | 'rights' | 'faab' | 'clause';
  text: string;
  position?: string;
  club?: string;
  /** Picks only: which of two in a round moves — "Ryan's own". */
  origin?: string;
  amount?: number;
}

/** One private counter-offer (2015-II-2), aimed at one of the two parties. */
export interface LiveTradeCounter {
  id: string;
  from: GmId;
  to: GmId;
  /**
   * A `match` is the bumped original partner's offer in 2015-II-3's final four hours. Same shape
   * as a counter and accepted the same way, but it comes from a different right and is subject to
   * a requirement a counter has none of — see `matchRight` in `domain/tradeWindow.ts`. Absent
   * means counter, so every offer written before the tail existed reads correctly.
   */
  kind?: 'counter' | 'match';
  sends: LiveTradeAsset[];
  receives: LiveTradeAsset[];
  at: string;
  state: 'open' | 'accepted' | 'declined';
}

/* ---------------------------------------------------------------------------------------- */

/**
 * One line in the append-only audit log.
 *
 * ONLY WHAT CANNOT BE DERIVED. Every other kind of change already leaves its own provenance on the
 * record it changed — a keeper slate knows who saved it, a FAAB line knows who recorded it — and
 * `domain/auditLog.ts` reads those directly. Writing a second copy of that history here would give
 * the log two sources for one fact and a way for them to disagree.
 *
 * So this collection holds sign-ins, page views, deletions and access decisions, and nothing else.
 */
export interface AuditEntry {
  id: string;
  season: number;
  at: string;
  /** The GM who did it, or the raw account id when the account is not yet paired to one. */
  actor: string;
  /** Whose record it was, when a commissioner acts on somebody's behalf. */
  onBehalfOf?: GmId;
  /**
   * `keeper` is the one written by a screen rather than derived from the record.
   *
   * A declaration holds only its current state, so the version before a save is gone the moment
   * the save lands and `domain/auditLog.ts` — which derives everything else from the records
   * themselves — can only ever say "submitted 6 keepers". What changed has to be written as it
   * happens, from the screen that still has both sides. See `domain/slateDiff.ts`.
   */
  kind: 'sign-in' | 'view' | 'delete' | 'access' | 'acting-as' | 'note' | 'keeper';
  /** One line, already written for a person to read. */
  what: string;
  /** `collection/id`, so a row can be traced to the record it describes. */
  target?: string;
  /**
   * Everything worth keeping that the sentence does not carry — the Yahoo display name and email
   * on an unpaired sign-in, the manager being impersonated on an `acting-as`. Rendered as a
   * key/value list when a row is opened.
   */
  detail?: Record<string, string>;
  /**
   * ACTING-AS ONLY. `start` opens a span and `stop` closes it, and the pair is what lets the log
   * say that a record naming John was written while Tim was acting as him.
   */
  span?: 'start' | 'stop';
}

/* ---------------------------------------------------------------------------------------- */

/**
 * FAAB movement. A running ledger rather than a balance, because the balance is contested
 * often enough that the audit trail matters more than the number: farm auction wins, waiver
 * claims, luxury tax, trades and in-draft purchases all touch it.
 */
export interface FaabEntry {
  id: string;
  season: number;
  gm: GmId;
  /** Negative spends, positive credits. */
  amount: number;
  kind: 'allotment' | 'purchase' | 'waiver' | 'farm-bid' | 'luxury-tax' | 'trade' | 'fine' | 'adjustment';
  description: string;
  date: string;
  recordedBy: GmId;
  /**
   * THIS ROW REVERSES THAT ONE — the id of the entry it cancels.
   *
   * How a commissioner edits a ledger he is not allowed to edit. `firestore.rules` says
   * `allow update, delete: if false` on `faab` on purpose, so a wrong line is corrected the way a
   * bookkeeper corrects one: a second line of equal and opposite amount, which nets the mistake to
   * nothing and leaves both halves of it visible. `domain/faabVoid.ts` writes them; the ledger
   * strikes the original through and stacks the reversal under it.
   */
  voids?: string;
}

/* ---------------------------------------------------------------------------------------- */

/** A nomination in the farm auction, live. One row per turn, including passes. */
/**
 * A GM's private watch list for the farm rounds.
 *
 * PRIVATE MEANS PRIVATE, INCLUDING FROM A COMMISSIONER. `firestore.rules` refuses every read but
 * the owner's, and the card says PRIVATE on it — a pill that promises something the rules do not
 * enforce is worse than no pill. The one thing the league can see is the auction itself, which is
 * why a row can strike through: the strikethrough is derived from the public nomination log, never
 * from anybody else's list.
 *
 * Deliberately NOT a bid, a price or a rank. The farm auction is live and open-outcry; anything
 * resembling a plan the app could enforce would be a second auction running beside the real one.
 * A name and a short note is the whole object.
 */
export interface FarmTarget {
  /** The college or high-school player, as the index spells him. */
  player: string;
  position?: string;
  school?: string;
  /** One short line, the GM's own words. Why he is watching this player. */
  note?: string;
  addedAt: string;
}

export interface FarmTargetList {
  season: number;
  gm: GmId;
  targets: FarmTarget[];
  updatedAt: string;
}

/**
 * ONE ENTRY ON A GM'S PICKLIST.
 *
 * The player's name is the identity, because that is what the board records and what the
 * "somebody took him" check compares. The rest is what the row needs to draw itself without
 * going back to the bank — a bank refresh must never silently change what a man queued.
 */
export interface PickListEntry {
  player: string;
  position?: string;
  nflTeam?: string;
  bye?: number | null;
}

/**
 * A GM's private queue of who he wants next (Tim, 6 September).
 *
 * PRIVATE, and the rules enforce it: a picklist read by the room is a list of players everybody
 * else now knows to take first, which is worse than no list at all. Same shape and the same
 * one-document-by-id access as `FarmTargetList`, for the same reason — there is deliberately no
 * plural method, so no screen can reach for everybody's.
 *
 * It never picks for anybody. When a GM's turn comes and the top of his list is still on the
 * board he is OFFERED it; the board waits for him to say yes.
 */
export interface PickList {
  season: number;
  gm: GmId;
  players: PickListEntry[];
  updatedAt: string;
}

export interface FarmNomination {
  season: number;
  round: number;
  slot: number;
  nominator: GmId;
  player?: string;
  position?: string;
  school?: string;
  winner?: GmId;
  bid?: number;
  passed: boolean;
  updatedAt: string;
  /**
   * WHO ACTUALLY WROTE IT, which is not always `nominator` (Tim, 22 August).
   *
   * A commissioner running the auction from the room enters nominations and bids on other GMs'
   * behalf, and until this field existed the record only knew whose nomination it WAS. The
   * activity log then read "John nominated a prospect" for a row Tim had typed. Optional, because
   * every row written before today has no answer and guessing one would be worse than the gap.
   */
  updatedBy?: GmId;

  /*
   * The live auction, per turns 13a/13c. `winner`/`bid` above are the OUTCOME and stay the record;
   * these three are the state of a sale in progress, and are what every screen in the room reads
   * between the nomination and the hammer.
   */
  /** The standing high bid. */
  highBid?: number;
  /** Who holds it. */
  highBidder?: GmId;
  /** Who has taken himself out of this one. Passing is a deliberate exit and is remembered. */
  passedBy?: GmId[];
  /**
   * When the standing bid was placed. Every raise resets it, and the going-gone clock counts from
   * here — so the last two bidders trading raises are never cut off mid-exchange.
   */
  bidAt?: string;
}

/* ---------------------------------------------------------------------------------------- */

/** One selection on the live board. */
export interface DraftPick {
  season: number;
  round: number;
  /** Pick within the round, 1-based in snake order. */
  pick: number;
  overall: number;
  /** Who owns the pick now — after trades, not necessarily the GM in that slot originally. */
  gm: GmId;
  player?: string;
  position?: string;
  nflTeam?: string;
  /** Filled automatically for a declared keeper rather than typed on draft night. */
  isKeeper: boolean;
  madeAt?: string;
  madeBy?: GmId;
}

export interface DraftState {
  season: number;
  /** Slot order for round one; even rounds reverse it. */
  order: GmId[];
  status: 'setup' | 'live' | 'complete';
  /** Overall pick number currently on the clock. */
  onTheClock: number;
  updatedAt: string;

  /**
   * Everything behind the 2020-II-1 draft position process. Kept as inputs plus a seed rather
   * than just the answer, so a disputed lottery can be re-run and shown to be the same one —
   * 2014-II-1 §5 wants it drawn in front of witnesses, and this is the online equivalent.
   */
  finishOrder?: GmId[];
  /** Winner of the consolation bracket, guaranteed second choice (2020-II-1 §3). */
  toiletBowlWinner?: GmId | null;
  /** By the postseason tournament, not the regular season (2018-II-5 §2). */
  thirdPlace?: GmId | null;
  fourthPlace?: GmId | null;
  /**
   * @deprecated A boolean cannot tell `declined` from `lapsed`, and Pass 06 §3 needs to.
   * Read through `thirdPlaceSwap.swapState` instead; kept only so a draw recorded before
   * 24 August still resolves the way it did on the night.
   */
  thirdFourthSwap?: boolean;
  /**
   * The third-place declaration — Pass 06 §3.
   *
   * FOUR STATES OUT OF ONE STORED FACT. Only the two answers a person can give are written down;
   * `pending` and `lapsed` are the absence of one, read against the deadline. Storing `lapsed`
   * would mean something has to run at 5:00 PM to write it, and a state that depends on a job
   * having fired is a state that can be wrong.
   *
   * `declaredVia` and `recordedBy` are what make a verbal declaration in a room as auditable as a
   * tap, which is the norm on committee night rather than the exception.
   */
  thirdFourthDeclaration?: {
    answer: 'taken' | 'declined';
    /** When he said it. */
    at: string;
    /** Whose declaration it is — always the third-place GM, even when a commissioner typed it. */
    gm: GmId;
    declaredVia: 'app' | 'verbal';
    /** The commissioner who recorded a verbal declaration, and when he typed it. */
    recordedBy?: GmId | null;
    recordedAt?: string | null;
  } | null;
  lotterySeed?: number | null;
  /**
   * THE FOUR THE MACHINE PRODUCED, stored with the result.
   *
   * Derivable from the seed, and stored anyway. Six seeds reproduce the 2026 board from three
   * different draws (`seedhunt.mts`), so the order alone cannot say who was drawn — and a
   * provenance that has to be recomputed is one that changes if `runLottery` ever does. What the
   * reveal draws its boundary from, and what decides whether the Toilet Bowl winner was placed or
   * won his way in. Absent on draws recorded before 27 August; the reveal falls back to the seed.
   */
  lotteryTop4?: GmId[];
  /** Who chooses their slot, in order. Empty until the lottery has been run. */
  selectionOrder?: GmId[];
  /**
   * When the draw was recorded, ISO. This is the BROADCAST: `draft-order-brief.md` §5 wants every
   * GM's screen to switch to the reveal at the same moment, and the reveal is ten rows filling on a
   * clock. Storing the instant rather than pushing a step means one write does it — every client
   * subscribed to `draftState` starts the same animation from the same origin, and a GM who opens
   * the app eight seconds late joins it already in progress rather than watching it from the top.
   *
   * Absent on a draw made before this field existed, which simply means no reveal is owed.
   */
  lotteryDrawnAt?: string | null;
  /** True once the commissioner has published the resolved order to the league. */
  published?: boolean;

  /* -- resolve at five, publish at eight (Pass 06 §7) --------------------------------------- */

  /**
   * When the scheduled resolve ran. Its PRESENCE is what makes `order` provisional rather than
   * public — there is no separate status field, because a status and a timestamp that can
   * disagree is a state machine with a hole in it.
   */
  resolvedAt?: string | null;
  /** When the order became the league's, and who or what published it. */
  publishedAt?: string | null;
  publishedBy?: GmId | 'schedule' | null;
  /**
   * A commissioner stopping the automatic publication, with a reason the league can read.
   *
   * A hold does not expire. One that lapsed on its own would publish exactly the disputed order
   * it was raised over, at the hour nobody was watching.
   */
  hold?: { reason: string; by: GmId; at: string } | null;
  /**
   * What the resolve read, frozen beside what it produced (`ResolutionSnapshot`).
   *
   * The seed makes the DRAW replayable; this makes the RESOLUTION replayable, which is what
   * September's argument is actually about. Written every time the resolve runs, so a correction
   * during the cure window leaves the superseded snapshot's `corrections` behind it.
   */
  resolution?: unknown;

  /**
   * When a commissioner last restarted the pick clock by hand.
   *
   * The clock's origin is normally the newest pick actually made. Somebody arguing for four
   * minutes, a phone that dropped, a lap of the room to find whose turn it is — none of those
   * are a pick, and none of them should leave the next man with eleven seconds. Restarting moves
   * the origin forward deliberately, with a record, rather than by the accident that used to
   * happen when clearing a cell stamped a fresh `madeAt`.
   */
  clockRestartedAt?: string;
  clockRestartedBy?: GmId;

  /**
   * WHEN THE DRAFT ACTUALLY BEGAN — stamped once, on the first Start, and never moved again.
   *
   * `clockRestartedAt` used to answer this, and it is the wrong field: a commissioner's
   * `Restart pick` moves it forward, and a resume after a pause used to as well. What needs
   * a fixed instant is the question "had the draft begun when this happened", and the thing
   * that made it matter was a trade (Tim, 8 September): picks typed into the board on the
   * evening BEFORE draft day are stamped with the wall clock, so a deal agreed at 6:59pm the
   * night before was reported as "announced at pick 43" on a draft that had not started.
   *
   * A pick entered before this instant is a pick pre-loaded onto the board, not a pick the
   * room has made. Absent on a draft that has never been started, and on one that went live
   * before this field existed — in which case `clockRestartedAt` is the closest true answer.
   */
  startedAt?: string;
  startedBy?: GmId;

  /**
   * EVERY TIME THE ROOM STOPPED, and when it started again.
   *
   * Pausing used to be `status: 'setup'` and nothing else, so resuming stamped
   * `clockRestartedAt` and every clock on the board went back to the top — including four
   * lapsed ones, whose elapsed figures were the only record of who the commissioner had to
   * chase (Tim, 6 September). The ledger makes a pause a thing that HAPPENED rather than a
   * state the board is in: the clocks count draft time, wall time minus these intervals, and
   * a pause of any length changes nobody's position.
   *
   * Append-only. The last entry has no `until` while the room is stopped.
   */
  pauses?: {
    at: string; until?: string; by?: GmId;
    /**
     * Set where the SCHEDULE stopped the room rather than a person: the round the intermission
     * was due after. It is what makes the stop idempotent — ten browsers all noticing the same
     * intermission must produce one pause, not ten — and it is why the banner can say the room is
     * at its break rather than merely stopped.
     */
    intermissionAfter?: number;
  }[];

  /**
   * THE FARM AUCTION'S OWN RUN STATE (Tim, 8 September: "need start/pause, reset pick, reset
   * draft, undo/redo controls on the farm rounds just like the main draft").
   *
   * The auction had no run state at all, and borrowed the draft's — which produced a quiet
   * contradiction it lived with all season: the farm screen read `paused = status !== 'live'`
   * while the same screen only UNLOCKS at `status === 'complete'`. So in every state where farm
   * bidding was legal the auction was paused by definition, and the going-gone clock counted down
   * in front of everybody and never once sold a lot.
   *
   * They are separate fields rather than a reuse of `status`/`pauses` because the two clocks run
   * at different times of the night and the main draft's ledger is the record of ITS evening: a
   * farm pause has no business shifting a lapsed pick's elapsed figure four hours after the fact.
   *
   * `farmPauses` is append-only, and the last entry has no `until` while the room is stopped —
   * the same shape as `pauses`, so `openPicks`-style arithmetic reads either.
   */
  farmStatus?: 'setup' | 'live' | 'complete';
  farmPauses?: { at: string; until?: string; by?: GmId }[];
  farmStartedAt?: string;
  farmStartedBy?: GmId;

  /**
   * "B drafts for A" — keyed by the GM being covered. Somebody misses draft night most years
   * and hands his board to a friend in the room; modelling it here beats sharing a login.
   */
  designates?: Record<GmId, GmId>;
}

/* ---------------------------------------------------------------------------------------- */

/**
 * A rule change somebody wants the Competition Committee to vote on.
 *
 * Deliberately thin. The debate happens in the meeting and in Discord; what the app is for is
 * making sure a proposal exists in writing before the deadline, attributed, with the rule it
 * amends named — which is the part that goes wrong every year.
 */
export interface RuleProposal {
  id: string;
  season: number;
  /** Whoever wrote it. 2020-I-1 §2 gives him a veto over modifications made in the meeting. */
  gm: GmId;
  /**
   * EVERYONE whose agreement the veto belongs to.
   *
   * Absent on an ordinary proposal, where the author is the only proposer and `gm` says so. It
   * exists for BRANCHES: an amendment rewritten in the room inherits the original proposers, and
   * that inheritance is what keeps the man who wrote the first version his 2020-I-1 veto over a
   * wording he did not agree to. See `branchFrom`.
   */
  proposers?: GmId[];
  /**
   * The docket item this was branched from, if it was.
   *
   * A branch takes its parent's number with a letter — `3` fails, `3a` passes — so the minutes
   * record one argument with two votes rather than two unrelated decisions on the same subject.
   */
  branchedFrom?: string;
  /** When the outcome was written down, for the line the minutes read back. */
  recordedAt?: string;
  /**
   * WHAT IT DOES, declared before the vote and applied by it.
   *
   * Optional on purpose: `6a` is explicit that the effect is a convenience, not a gate — an
   * undeclared item can still be voted on, and most of the rulebook is `records only` anyway.
   * What it buys is the other half of the mechanism: a passed rule with an effect writes a value
   * for the season it names, and one that cannot be applied by changing a number carries the list
   * of what has to be built before it is real. See `src/domain/effects.ts`.
   */
  effect?: ProposalEffect;
  title: string;
  body: string;
  /** Rule ids this would amend or replace, if any. */
  amends: string[];
  /**
   * The life of a proposal, in the order it happens:
   *   draft       the author is still writing; nobody else sees it
   *   submitted   in front of the commissioners, wording still open to edit
   *   docketed    on the agenda; the league can read it
   *   passed / provisional / failed / withdrawn — after the vote
   *
   * `provisional` is a real outcome rather than a flavour of passed: 2015-I-1 puts a six-vote
   * rule in effect for one season and on next year's agenda automatically.
   */
  status: 'draft' | 'submitted' | 'docketed' | 'passed' | 'provisional' | 'failed' | 'withdrawn';
  /**
   * WHEN ITS WORDING WAS CHANGED IN THE ROOM, which starts a 24-hour clock (2019-I-2 §3).
   *
   * A modification made at the meeting goes to the GMs who could not attend, who "have 24 hours
   * to provide a vote on the final wording". So an amended item is HELD OPEN: it keeps its tally,
   * it cannot be recorded, and the rulebook is not written until the window closes. Every proxy
   * instruction on that item is voided at the same moment — the instruction was a position on
   * different words. 2020-I-1 separately requires the original proposer's support before any such
   * modification, which gates the amendment rather than this field.
   */
  amendedAt?: string;
  /** True when this proposal IS the mandatory re-vote of last year's provisional pass. */
  isRevote?: boolean;
  /** Set when a commissioner edits the wording, so the author can see it changed. */
  editedBy?: GmId;
  editedAt?: string;
  /**
   * WHO TYPED IT IN, when that was not the man who wrote it (Tim, 22 August).
   *
   * The 2026 proposals reached Vishal and Rajan by email and Discord months before this screen
   * existed, so a commissioner has to be able to enter one and have it read as ARI'S PROPOSAL —
   * not his own. `gm` stays the author, because 2020-I-1 §2 gives the author a veto over
   * modifications made in the room, and a proposal filed under the typist's name would put that
   * veto in the wrong hands.
   *
   * DIFFERENT FROM `editedBy`, which means somebody changed the WORDING of a proposal that was
   * already here. This one means the proposal arrived by some other route and was transcribed.
   */
  enteredBy?: GmId;
  enteredAt?: string;
  /**
   * How it reached the commissioners, in the entering commissioner's words — "emailed 3 July",
   * "Discord, #rules". Free text, because the answer is a sentence and inventing a vocabulary for
   * it would mean choosing between "email" and "the email Rajan forwarded from Ari".
   */
  enteredVia?: string;
  /** Recorded during the meeting. Keyed by GM; anyone absent is simply missing. */
  votes?: Record<GmId, 'yes' | 'no' | 'abstain'>;
  /**
   * WHO PRESSED THE BUTTON, which is not always whose vote it is.
   *
   * A commissioner records a vote for a GM who is not in the room. The vote is that GM's; the act
   * is the commissioner's, and the minutes have to be able to say so. Keyed by the GM whose vote
   * it is, valued with the actor — so `castBy[RYAN] === 'RAJAN'` renders "recorded by Rajan", and
   * an entry equal to its own key is somebody voting for himself.
   */
  castBy?: Record<GmId, GmId>;
  castAt?: Record<GmId, string>;
  /**
   * Private, before the meeting. A GM may record what he currently thinks and why, and may choose
   * to let the commissioners see it on the day. It is NOT a vote: it never counts towards a
   * threshold, and `tallyFrom` never sees this field.
   */
  provisional?: Record<GmId, { vote: 'yes' | 'no' | 'abstain'; note?: string; shared?: boolean }>;
  /** GMs represented at the meeting by any means — drives the relaxed threshold (2015-I-1). */
  represented?: number;
  /** 2020-I-1 §2: an original proposer is against a modification made in the room. */
  proposerObjects?: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * A question about a rule, answered mid-season by a panel of three (2015-I-4).
 *
 * Stored per season because that is exactly how long it binds: the panel answers for the rest of
 * the year, earlier weeks are not re-scored, and it does not carry forward on its own — it goes on
 * the next committee docket instead, because only the committee writes rule text.
 */
export interface InterpretationDoc {
  id: string;
  season: number;
  /** The rule it is about, and the section if the question is about one. */
  ruleId: string;
  section?: string;
  question: string;
  raisedBy: GmId;
  raisedAt: string;
  /** Each panelist's reading, in his own words. Free text — this is not a yes/no question. */
  answers?: Record<GmId, string>;
  /** Set once a commissioner records it. */
  decision?: string;
  decidedAt?: string;
  decidedBy?: GmId;
  /** Trades or other business held up until this is answered. */
  blocking?: string[];
  updatedAt: string;
}

/** One agenda, published once and then voted through. */
export interface Docket {
  season: number;
  /** Ordered proposal ids. The commissioners set the running order. */
  order: string[];
  publishedAt?: string;
  publishedBy?: GmId;
  /** Set when the meeting starts, so the vote screens know the relaxed threshold applies. */
  represented?: number;
  /**
   * WHO is not represented, rather than only how many.
   *
   * The count is what the thresholds turn on, but the screen needs the names: 5b draws an absent
   * GM a dashed "not represented" cell instead of three dead buttons, and a commissioner marking
   * attendance is thinking about people rather than about a number. `represented` stays as the
   * derived count so nothing that reads it has to change.
   */
  absent?: GmId[];
  /**
   * THE ITEM THE MEETING IS ON, and the most load-bearing field on this page.
   *
   * Voting is reachable on this proposal and no other. That is not a UI convenience: the meeting
   * is a discussion, and a docket everybody can vote through in advance is a docket nobody argues
   * about. Nothing is votable until a commissioner opens an item, and opening the next closes the
   * last.
   */
  openItemId?: string;
  /** Items already voted, so the meeting can show what is behind it. Set as each one closes. */
  closedIds?: string[];
  /** Set when the first item opens — the difference between a published docket and a meeting. */
  meetingOpenedAt?: string;
  /**
   * WHEN THE CURRENT ITEM WAS OPENED, for the elapsed clock on the live strip.
   *
   * Not `updatedAt`: that moves every time anybody marks attendance or edits an order, so a clock
   * reading from it would reset itself mid-argument and answer "how long has this been open" with
   * "however long since the last keystroke". Cleared when the item closes, because a clock with
   * nothing running is a number that means nothing.
   */
  openedAt?: string;
  /** Set by "Commit to record". The one irreversible step; after it the docket is history. */
  committedAt?: string;
  committedBy?: GmId;
  updatedAt: string;
}

/* ---------------------------------------------------------------------------------------- */

/**
 * A manager attached to a team name in a season Yahoo will not name one for.
 *
 * Before 2009 the archives have team names and nothing else, so the records engine has to refuse
 * to count 2003-2008 — and two stray teams in 2009/2010. This is the human answer to that, and
 * it is the only piece of history in the app that cannot be derived from a source: it comes out
 * of somebody's memory, so it records whose.
 *
 * Keyed on season + team NAME rather than Yahoo's team id, because the name is the only field
 * the three archives share.
 */
/**
 * A manager added or amended in the app.
 *
 * Layers over the roster that ships with the build. Two things bring somebody here: a new
 * manager joining the league, and a name from 2003-2008 that somebody finally remembers — the
 * pairing screen cannot pair a team to a man the app has never heard of.
 *
 * Nothing is ever deleted, only marked `former`: his seasons are still in the record, and a
 * career table cannot cite a manager the roster has forgotten.
 */
/**
 * The seasonless `league/config` document. Admin-written; the Cloud Functions read it.
 *
 * NOT `LeagueConfig` — `domain/config.ts` already exports that name for the league's RULES
 * constants (team count, keeper limits, the tax table), and two different shapes under one name in
 * one codebase is a mis-import waiting to happen.
 */
export interface LeagueRoles {
  adminGms: GmId[];
  commissionerGms: GmId[];
  /**
   * Closed to NEW accounts. An account the server has already seen keeps working, so a manager who
   * lost his session is never locked out by this — see `domain/access.ts`.
   */
  accessLocked?: boolean;
  lockedBy?: GmId;
  lockedAt?: string;
  seededAt?: string;
}

export interface LeagueManager {
  /** Permanent. Pairings, trades and declarations are all keyed on it. */
  id: GmId;
  name: string;
  status: 'current' | 'former';
  role?: Role;
  /** The current season's seat. Required for a current manager. */
  team?: string;
  mark?: string;
  order?: number;
  seasons?: number[];
  note?: string;
  addedBy: GmId;
  addedAt: string;
}

export interface TeamPairing {
  season: number;
  /** As the archive spells it. Matching is case- and whitespace-insensitive. */
  team: string;
  gm: GmId;
  pairedBy: GmId;
  pairedAt: string;
  /** How they knew — worth keeping when the answer is "Rajan remembered the trade". */
  note?: string;
}

/* ---------------------------------------------------------------------------------------- */

/** Commissioner annotations that belong with the data rather than in a Discord scroll. */
export interface AdminNote {
  id: string;
  season: number;
  /** What it is attached to: a player, a GM, a rule id, or the league generally. */
  subject: string;
  kind: 'ruling' | 'correction' | 'question' | 'penalty';
  body: string;
  citations: string[];
  createdBy: GmId;
  createdAt: string;
  resolved: boolean;
}

/* ---------------------------------------------------------------------------------------- */

/**
 * League settings the commissioner controls. The deadline is the important one: declarations
 * are open until he says otherwise, and the app should refuse writes afterwards rather than
 * relying on everyone remembering. Last year the deadline was announced three times in Discord
 * and people were still editing at 11:59.
 */
export interface LeagueSettings {
  season: number;
  /**
   * ISO INSTANTS, always. The rulebook says "5:00pm" and never says where, the league is spread
   * across Mountain and Eastern, and a wall-clock string would mean something different to Tim
   * than to everyone else. Stored as one true moment; rendered in both zones by
   * src/domain/deadlines.ts.
   */
  keeperDeadline: string | null;
  /** Set once the commissioner wants to freeze everything regardless of the clock. */
  declarationsLocked: boolean;
  /**
   * THE THIRD STATE OF A GATE (Tim, 3 September): a commissioner holding it open past its date.
   *
   * Follows the date · locked early · held open. The first two were a date and a boolean; this is
   * the one nothing could express, and it exists because the alternative — moving the announced
   * deadline for all ten to let the league back in — is worse than saying what happened. It names
   * who did it, when, and the deadline it overrode, and `firestore.rules` reads it too, so a
   * manager's write after the date is actually accepted rather than refused behind an open-looking
   * screen. Moving the date spends it: see `deadlines.exceptionStands`.
   *
   * Absent or null means the gate follows the date. Never both this and the lock: the form keeps
   * them exclusive, and where they disagree the exception wins.
   */
  declarationsHeldOpen?: GateException | null;
  /** 2020-II-1 §5: draft-slot preferences are due separately from the keeper slates. */
  preferenceDeadline: string | null;
  preferencesLocked?: boolean;
  preferencesHeldOpen?: GateException | null;
  /**
   * Rule proposals for the Competition Committee. 2022 "Proposals Of Rules" requires them in
   * writing ahead of the meeting; in practice they arrive in Discord the night before and
   * somebody has to collate them, which is what this date exists to stop.
   */
  proposalDeadline: string | null;
  /**
   * Proposals had no lock at all until Pass 15 — `Proposals.tsx` passed a hard-wired `false` and
   * the database never checked the date. Both halves exist now, same shape as the other two gates.
   */
  proposalsLocked?: boolean;
  proposalsHeldOpen?: GateException | null;
  /** When the committee actually meets. Shown next to the proposal deadline. */
  committeeDate: string | null;
  /**
   * The way IN to that meeting — a Zoom/Meet/Teams URL, or whatever the commissioners are using
   * this year.
   *
   * It lives in settings rather than in Discord because the meeting is the one event in the
   * league's calendar that half the managers attend from a phone, five minutes late, having lost
   * the message with the link in it. One field, set beside the date it belongs to, rendered as a
   * button on Home for the fortnight the meeting is the thing happening.
   *
   * NOT VALIDATED INTO A SHAPE. A commissioner may paste a Zoom link, a Meet link, or a line of
   * dial-in digits; refusing anything that is not a URL would be the app deciding how the league
   * meets. It is rendered as a link when it parses as one and as text when it does not.
   */
  committeeLink?: string | null;
  draftDate: string | null;

  /**
   * Draft-night clocks. There is NO RULE about any of this — the rulebook has nothing on pick
   * clocks, bid clocks or intermissions — so these are operational settings a commissioner sets,
   * and every one of them is ADVISORY: an expired pick clock does not auto-pick, it just goes red.
   * The one exception is the farm bid clock, which does end a lot, because an auction that never
   * terminates is not an auction.
   */
  clocks?: DraftClocks;

  updatedAt: string;
  updatedBy: GmId;
}

export interface ClockBand {
  /** Inclusive. */
  fromRound: number;
  /** Inclusive. */
  toRound: number;
  seconds: number;
}

export interface DraftClocks {
  /**
   * How long a pick has, by round. Bands rather than one number or a fixed two-tier, because the
   * league has never written this down and the first rounds get talked about while round 14 does
   * not — so the commissioner says where the line is. A round covered by no band has no clock.
   */
  pickBands?: ClockBand[];
  /** A scheduled intermission: after this round, for this many minutes. The pizza case. */
  intermissionAfterRound?: number | null;
  intermissionMinutes?: number | null;
  /** How long a GM has to name a farm nomination. Advisory. */
  nominateSeconds?: number | null;
  /**
   * A standing farm bid SELLS if nobody raises for this long. Not advisory — this is what ends a
   * lot. Each bid resets it.
   */
  bidSellsAfterSeconds?: number | null;
}

/**
 * The one interface the UI talks to. Two implementations: an in-memory store that makes the
 * single-file build fully explorable offline, and Firestore for the deployed app. The UI can
 * never tell which it is using, so the offline preview really is a preview.
 */
/**
 * A manager's picture.
 *
 * A DATA URL IN FIRESTORE, and not a bucket. The whole set is ten small JPEGs — a 96px square at
 * quality .82 is about eight kilobytes, so the entire league fits inside one Firestore document's
 * worth of quota with room to spare. A Storage bucket would mean another product to configure,
 * another set of rules to get right, another thing to be down on draft night, and a CORS story,
 * all to hold eighty kilobytes. If somebody one day wants full-size photos this is the wrong
 * shape and should be moved; for a 28px badge in a tab bar it is exactly right.
 *
 * The image is always produced by `avatarImage.ts`, never taken from the file the person chose:
 * an untouched phone photo is four megabytes and would be refused by Firestore, silently, at the
 * moment somebody was trying to be friendly with the app.
 */
export interface Avatar {
  /** Uppercase GM id. Also the document id, so ownership is checked against the path. */
  gm: GmId;
  /** `data:image/jpeg;base64,...`, square, 96px, written by `toAvatar()`. */
  image: string;
  updatedAt: string;
  /** Who wrote it — a commissioner can set one for a manager who cannot work the upload. */
  updatedBy: GmId;
}

export interface LeagueStore {
  readonly kind: 'memory' | 'firestore';

  settings(season: number): Promise<LeagueSettings | null>;
  saveSettings(s: LeagueSettings): Promise<void>;

  /**
   * Every SUBMITTED slate — the set the league is allowed to see.
   *
   * A draft is private to its manager until he sends it. That is enforced in `firestore.rules`,
   * which means a listen on the whole collection fails once anybody has a draft open; both stores
   * therefore answer this the same narrowed way. If you want your own slate, submitted or not,
   * that is `declarationFor`.
   */
  declarations(season: number): Promise<KeeperDeclaration[]>;
  /** One manager's slate, draft included. Readable by him and by a commissioner, nobody else. */
  declarationFor(season: number, gm: GmId): Promise<KeeperDeclaration | null>;
  saveDeclaration(d: KeeperDeclaration): Promise<void>;

  preferences(season: number): Promise<DraftPreference[]>;
  savePreference(p: DraftPreference): Promise<void>;

  /*
   * PROXY BALLOTS — see `domain/proxy.ts`, and 2015-I-1, which counts a proxy toward the quorum
   * of eight. Narrowed the same way declarations are, and for the same reason: an instruction is
   * private to its author until it is lodged, so a listen on the whole collection would fail the
   * moment anybody had one written. `proxyFor` is how a GM reads his own.
   */
  proxies(season: number): Promise<ProxyBallot[]>;
  proxyFor(season: number, gm: GmId): Promise<ProxyBallot | null>;
  saveProxy(b: ProxyBallot): Promise<void>;

  trades(season: number): Promise<Trade[]>;
  /**
   * Every trade, all seasons. The log is a history view — a trade agreed in November about next
   * year's picks is filed under next year, so a season-bucketed read would hide it from the very
   * page whose job is to show it.
   */
  tradesAll(): Promise<Trade[]>;
  saveTrade(t: Trade): Promise<void>;
  deleteTrade(season: number, id: string): Promise<void>;

  faab(season: number): Promise<FaabEntry[]>;
  saveFaab(e: FaabEntry): Promise<void>;
  /**
   * Remove a ledger row for good.
   *
   * There is no `updateFaab` beside it, and that is the rule rather than an omission:
   * `firestore.rules` allows create and delete on `faab` and refuses update, so a corrected row is
   * a removal and a fresh row — two audit entries that cannot be mistaken for the line having
   * always said that. See the note above the `faab` block in the rules.
   */
  deleteFaab(season: number, id: string): Promise<void>;

  farmNominations(season: number): Promise<FarmNomination[]>;
  saveFarmNomination(n: FarmNomination): Promise<void>;

  /*
   * A GM's own farm watch list, and there is deliberately no way to read anybody else's. The
   * plural method every other collection has is missing on purpose: there is no legitimate caller
   * for "everyone's targets", and providing one would be an invitation for a screen to try.
   */
  farmTargetsFor(season: number, gm: GmId): Promise<FarmTargetList | null>;
  saveFarmTargets(t: FarmTargetList): Promise<void>;

  /**
   * A GM's own picklist, and — exactly as with `farmTargetsFor` — there is no way to read
   * anybody else's. No plural method exists, on purpose.
   */
  pickListFor(season: number, gm: GmId): Promise<PickList | null>;
  savePickList(l: PickList): Promise<void>;

  draftState(season: number): Promise<DraftState | null>;
  saveDraftState(s: DraftState): Promise<void>;
  picks(season: number): Promise<DraftPick[]>;
  savePick(p: DraftPick): Promise<void>;

  notes(season: number): Promise<AdminNote[]>;
  saveNote(n: AdminNote): Promise<void>;

  /**
   * Mid-season interpretations (2015-I-4). A question about a rule, the three-man panel that
   * answered it, and how the rule reads for the rest of the season.
   */
  interpretations(season: number): Promise<InterpretationDoc[]>;
  saveInterpretation(i: InterpretationDoc): Promise<void>;

  /**
   * Values a commissioner set by hand, against what the rules produce. Append-mostly: a revert
   * marks the row rather than removing it, because the history is the whole point.
   */
  overrides(season: number): Promise<Override[]>;
  saveOverride(o: Override): Promise<void>;

  /**
   * A manager's private guesses at everybody else's keepers. Read your own; there is no method
   * to read another man's, and the rules file refuses one anyway.
   */
  mockKeepers(season: number, gm: GmId): Promise<MockKeeperSet | null>;
  saveMockKeepers(m: MockKeeperSet): Promise<void>;

  /**
   * The NFL player pool the draft board searches. Null means "nobody has refreshed it", and the
   * UI falls back to the snapshot compiled into the build — which is the normal state right up
   * until somebody presses refresh.
   */
  playerBank(season: number): Promise<PlayerBank | null>;
  savePlayerBank(b: PlayerBank): Promise<void>;

  /**
   * The college/high-school prospect pool the farm rounds search. Same fallback rule as the NFL
   * bank: null means nobody has edited it and the build-time snapshot stands.
   */
  prospectBank(season: number): Promise<ProspectBank | null>;
  saveProspectBank(b: ProspectBank): Promise<void>;

  /**
   * The audit log — APPEND ONLY, and admin-read only.
   *
   * It holds the four things the derived timeline cannot see: a sign-in, a page view, a deletion,
   * and an access decision. Everything else on `Admin › Activity` is read off the records
   * themselves (`domain/auditLog.ts`), which is both retroactive and impossible to falsify by
   * writing a row.
   *
   * Nothing updates or deletes here, ever — enforced in `firestore.rules`, not by convention. A
   * log somebody can edit is not a log.
   */
  audit(season: number): Promise<AuditEntry[]>;
  recordAudit(e: AuditEntry): Promise<void>;

  /**
   * Trades in flight. Read whole for the season — there are never more than a handful open, and
   * the screen has to decide visibility per viewer, which it cannot do from a filtered query.
   */
  liveTrades(season: number): Promise<LiveTradeDoc[]>;
  saveLiveTrade(t: LiveTradeDoc): Promise<void>;
  deleteLiveTrade(season: number, id: string): Promise<void>;

  proposals(season: number): Promise<RuleProposal[]>;
  saveProposal(p: RuleProposal): Promise<void>;
  deleteProposal(season: number, id: string): Promise<void>;

  docket(season: number): Promise<Docket | null>;
  saveDocket(d: Docket): Promise<void>;

  /**
   * Historical manager pairings. Not per-season like everything else — the whole point is to
   * answer for seasons other than the current one, so the collection is read whole.
   */
  teamPairings(): Promise<TeamPairing[]>;
  saveTeamPairing(p: TeamPairing): Promise<void>;
  deleteTeamPairing(season: number, team: string): Promise<void>;

  /**
   * Manager pictures, keyed by GM. Not season-scoped — a face does not expire with a season.
   * Absent from the map means nobody has set one and the initials badge stands in.
   */
  avatars(): Promise<Avatar[]>;
  saveAvatar(a: Avatar): Promise<void>;
  clearAvatar(gm: GmId): Promise<void>;

  /** The roster overlay: managers added or amended after the build. Not season-scoped. */
  managers(): Promise<LeagueManager[]>;
  saveManager(m: LeagueManager): Promise<void>;

  /**
   * `league/config` — the one seasonless document.
   *
   * It already held `adminGms` and `commissionerGms`, written once at seed time and read by the
   * Cloud Functions to decide who may confirm a link. `accessLocked` joins them because the server
   * has to read it too — `POST /api/link` refuses to create a row for a new account while it is on
   * — and a season-scoped setting would mean the server guessing which season it is.
   */
  leagueConfig(): Promise<LeagueRoles | null>;
  saveLeagueConfig(c: LeagueRoles): Promise<void>;
  /** Direct read: does the settings doc exist? Guards the seed — see `seedCalendar`. */
  hasSettings(season: number): Promise<boolean>;
  /**
   * Remove a manager added in the app.
   *
   * Only ever for a mistake. A manager with any history is retired, not deleted — his seasons are
   * in the record and a career table cannot cite somebody the roster has forgotten. The UI checks
   * for references first and offers retire instead when it finds any.
   */
  deleteManager(id: GmId): Promise<void>;

  /**
   * Live updates. Draft night is ten people typing into the same board, so the board cannot
   * be a polling loop. Returns an unsubscribe function. The memory store fires on local
   * writes only, which is enough to develop against.
   */
  subscribe(season: number, onChange: (what: StoreCollection) => void): () => void;
}

/** Every collection name, for a restore that has to repaint the whole app at once. */
export const ALL_COLLECTIONS = [
  'settings', 'declarations', 'preferences', 'proxies', 'trades', 'faab', 'farmNominations', 'draftState',
  'picks', 'notes', 'playerBank', 'prospectBank', 'proposals', 'docket', 'interpretations',
  'overrides', 'mockKeepers', 'teamPairings', 'managers', 'avatars', 'liveTrades', 'audit',
] as const;

export type StoreCollection =
  | 'settings' | 'declarations' | 'preferences' | 'proxies' | 'trades' | 'faab'
  | 'farmNominations' | 'farmTargets' | 'pickList' | 'draftState' | 'picks' | 'notes' | 'playerBank'
  | 'prospectBank'
  | 'proposals' | 'docket' | 'interpretations' | 'overrides' | 'mockKeepers'
  | 'teamPairings' | 'managers' | 'avatars' | 'liveTrades' | 'audit';
